import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";

export type ComponentStatus = "Healthy" | "Degraded" | "Down" | "Unknown";

export interface PlatformOverview {
  generatedAt: string;
  version: string;
  environment: string;
  components: Array<{ key: "api" | "database" | "storage" | "jobs" | "realtime" | "llm"; status: ComponentStatus; detail?: string | null; latencyMs?: number | null }>;
  queues: Array<{ name: string; enqueued: number; processing: number; failed: number; succeeded24h: number }>;
  agents: Array<{ key: string; name: string; runs24h: number; successRate: number | null; p95Ms: number | null; lastFailureAt?: string | null; lastError?: string | null; configurable: boolean }>;
  connectors: Array<{ key: string; name: string; health: string; breaker: "Closed" | "Open" | "HalfOpen"; avgLatencyMs: number | null; lastCallAt?: string | null; failureRate24h: number | null }>;
  providers: Array<{ key: string; name: string; kind: string; residency: string; active: boolean; routes: number; offline: boolean }>;
  pendingChangeSets: Array<{ id: string; title: string; kind: string; items: number; requestedBy: string; requestedAt: string; effectiveFrom?: string | null }>;
  recentChanges: Array<{ id: string; at: string; actor: string; action: string; target: string; detail?: string | null }>;
  drift: Array<{ dataset: string; from: number; to: number; at: string }>;
  spend: { last30dUsd: number; byTier: Array<{ tier: string; usd: number }>; daily: Array<{ day: string; usd: number }> };
  runningCases: Array<{ workflowKey: string; workflowName: string; version: number; isActive: boolean; cases: number }>;
  /** One card per workspace: adoption of the AI Workspace, spend against budget and what awaits review. */
  workspaces: OverviewWorkspace[];
}

export interface OverviewWorkspace {
  key: string;
  name: string;
  nameAr?: string | null;
  icon: string;
  color: string;
  kind: string;
  enabled: boolean;
  assistants: number;
  members: number;
  threads: number;
  threadsThisWeek: number;
  activeUsers7d: number;
  spendMonthUsd: number;
  monthlyBudgetUsd: number;
  pendingProposals: number;
  delegatedRunsMonth: number;
  artifacts: number;
}

export function usePlatformOverview() {
  const api = useApi();
  return useQuery({
    queryKey: ["platform", "overview"],
    queryFn: () => api.get<PlatformOverview>("/api/platform/overview"),
    refetchInterval: 30_000,
  });
}
